<template>
    <div id="app">
        <structured-data />
        <!-- Home nav bar -->
        <div v-if="!globalRefs.isLoggedIn && isHome()" class="home-bg p-2 px-md-4 sticky-top" :class="{ shadow: scrolled, 'sticky-top': !isMobile }">
            <div class="container-lg d-flex flex-row align-items-center">
                <h4 class="my-0 me-auto font-weight-normal">
                    <a href="/" aria-label="Home"><img width="269" height="67" class="title-img" alt="Logo" :src="isDarkMode ? '/logo-light.png' : '/logo.png'" /></a>
                </h4>
                <nav class="my-md-2 d-none d-sm-block">
                    <a class="ps-md-4 ps-3 nav-text" href="https://blog.dateit.com">Blog</a>
                    <a class="ps-md-4 ps-3 nav-text" href="/docs">Help</a>
                    <a class="ps-md-4 ps-3 nav-text" href="/about">About</a>
                </nav>
                <ThemeToggle class="ms-3 me-3 nav-text" />
                <b-button v-if="!globalRefs.isLoggedIn && !isEmbeddedBrowser" class="btn nav-button btn-primary" v-on:click="login()">Login</b-button>
            </div>
        </div>
        <!-- App nav bar -->
        <div v-if="globalRefs.isLoggedIn || !isHome()" class="app-bg p-2 px-md-4 z1" :class="{ shadow: scrolled, 'fixed-top': !isMobile }">
            <div class="container-lg d-flex flex-row align-items-center">
                <h4 class="my-0 me-auto font-weight-normal">
                    <a href="/" aria-label="Home"><img width="269" height="67" class="title-img" alt="Logo" :src="'/logo-light.png'" /></a>
                </h4>
                <nav class="my-md-2 me-md-3 me-xs-3">
                    <a class="ps-md-4 ps-3 text-light nav-link-blog" href="https://blog.dateit.com">Blog</a>
                    <a class="ps-md-4 ps-3 text-light nav-link-help" href="/docs">Help</a>
                    <a class="ps-md-4 ps-3 text-light nav-link-about" href="/about">About</a>
                </nav>
                <ThemeToggle class="ms-1 me-3 text-light theme-toggle-responsive" :isDarkNav="true" />
                <b-button v-if="!globalRefs.isLoggedIn && !isEmbeddedBrowser" class="btn nav-button btn-primary" v-on:click="login()">Login</b-button>
                <a v-if="globalRefs.isLoggedIn" class="me-xs-3 btn btn-success nav-button" @click="createEvent" data-dd-action-name="New Event Custom">New Event</a>
                <b-dropdown v-if="globalRefs.isLoggedIn && authManager.metadata" :key="`dropdown-${authManager.displayPhoto}-${authManager.displayName}`" id="dropdown-1" variant="link" toggle-class="text-decoration-none" class="account-button" offset="8" no-caret>
                    <template #button-content>
                        <avatar :name="authManager.displayName" :size="40" :photoUrl="authManager.displayPhoto" class="avatar-img-header"></avatar>
                    </template>
                    <b-dropdown-item class="no-link" href="/preferences">Preferences</b-dropdown-item>
                    <b-dropdown-item v-if="authManager.metadata?.plan === 'development'" href="/admin">Developer console</b-dropdown-item>
                    <b-dropdown-item v-on:click="authManager.signOut()">Sign Out</b-dropdown-item>
                </b-dropdown>
            </div>
        </div>
        <div v-if="globalRefs.isLoggedIn || !isHome()" :class="{ shadow: scrolled, spacer: !isMobile }"></div>
        <login-popup ref="loginPopup"></login-popup>
        <create-edit-event ref="createEventPopup"></create-edit-event>
        <router-view />
        <div id="content-bottom"></div>
        <footer>
            <div id="footer" class="box footer z1" v-bind:style="{ 'margin-top': footerPadding + 'px' }">
                <div class="footer-text">
                    <span class="footer-copyright">© {{ new Date().getFullYear() }} dateit LLC</span>
                    <span class="footer-links">
                        <a class="footer-link" href="/privacy">Privacy Policy</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="/tos">Terms of Service</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="/affiliate">Affiliate</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="/docs">Documentation</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="https://dateit.printful.me/">Merch</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="https://blog.dateit.com/">Blog</a>
                        <span class="footer-divider">·</span>
                        <a class="footer-link" href="/about">About</a>
                        <span class="footer-divider">·</span>
                        <ThemeToggle class="nav-text d-inline" />
                    </span>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import LoginPopup from "./components/LoginPopup.vue";
import Avatar from "./components/Avatar.vue";
import Utils from "./util/Utils";
import CreateEditEvent from "@/components/CreateEditEvent.vue";
import StructuredData from "@/components/StructuredData.vue";
import { datadogRum } from "@datadog/browser-rum";
import { nextTick, inject, reactive } from "vue";
import { authUi } from "@/AuthManager";
import { mobileUtil } from "@/util/MobileUtil";
import ThemeToggle from "@/components/ThemeToggle.vue";
import { useTheme } from "@/composables/useTheme";

export default {
    components: {
        LoginPopup,
        CreateEditEvent,
        Avatar,
        StructuredData,
        ThemeToggle,
    },
    mixins: [Utils],
    created() {
        window.addEventListener("scroll", this.handleScroll);
    },
    destroyed() {
        window.removeEventListener("scroll", this.handleScroll);
    },
    data() {
        return {
            scrolled: false,
            userPhoto: "",
            footerPadding: 0,
        };
    },
    setup() {
        const api = inject("api");
        if (!api) {
            throw new Error("API not provided");
        }
        const authManager = inject("authManager");
        if (!authManager) {
            throw new Error("API not provided");
        }

        const globalRefs = inject("globalRefs");
        if (!globalRefs) {
            throw new Error("API not provided");
        }

        const { isMobile, isEmbeddedBrowser } = mobileUtil();
        const { isDarkMode } = useTheme();

        return {
            api,
            authManager,
            globalRefs,
            isMobile,
            isEmbeddedBrowser,
            isDarkMode,
        };
    },
    async mounted() {
        // Initialize theme
        useTheme();

        this.$watch(
            () => this.authManager.isLoggedIn,
            (loggedIn) => {
                this.globalRefs.isLoggedIn = loggedIn;
            },
            { immediate: true },
        );

        // react to resize and any dom change
        const observer = new MutationObserver(this.handleResize);
        observer.observe(document.body, { childList: true, subtree: true });
        window.addEventListener("resize", this.handleResize);

        if (authUi.isPendingRedirect()) {
            this.login();
        }

        let query = Object.assign({}, this.$route.query);
        if (query.qr != null) {
            delete query.qr;
            datadogRum.addAction("QR Scanned");

            this.$router
                .replace({
                    query,
                })
                .catch(() => {});
        }

        if (query.ins != null) {
            delete query.ins;
            datadogRum.addAction("Instagram Ad Click");
            this.$router
                .replace({
                    query,
                })
                .catch(() => {});
        }

        var myRegexp = RegExp(/\/e\/.*dateit\.(io|com)\/e\/(\w*)/);
        var matches = myRegexp.exec(window.location.pathname);
        if (matches != null && matches.length == 3) {
            this.$router.push("/e/" + matches[2] + window.location.search);
            datadogRum.addAction("fix_path_and_redirect");
        }

        await nextTick();

        this.globalRefs.loginPopUpUi = this.$refs.loginPopup;
        this.globalRefs.createEditEventUi = this.$refs.createEventPopup;
    },
    beforeUnmount() {
        const observer = new MutationObserver(this.handleResize);
        observer.disconnect();
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {
        isHome() {
            return this.$route.name === "Home";
        },
        handleScroll() {
            this.scrolled = window.scrollY > 0;
        },
        appstoreLink() {
            if (this.isAndroid) {
                return "https://play.google.com/store/apps/details?id=io.dateit.app.android";
            }
            return "https://apps.apple.com/us/app/dateit/id1610780514";
        },
        login() {
            this.$refs.loginPopup.showLoginUi();
        },
        createEvent() {
            this.$refs.createEventPopup.showUi();
        },
        handleResize() {
            if (document.querySelector("#footer") == null || document.querySelector("#content-bottom") == null) {
                return;
            }
            const height = document.querySelector("#footer").offsetHeight;
            const selector = document.querySelector("#content-bottom");

            const distanceToBottom = window.innerHeight - selector.getBoundingClientRect().bottom;
            const offset = distanceToBottom - height;
            if (offset > 0) {
                this.footerPadding = offset;
            } else {
                this.footerPadding = 0;
            }
        },
    },
};
</script>

<style>
html,
body {
    background: var(--bg-primary);
    /* height: 100%; */
}
.spacer {
    height: 56px;
    width: 100%;
}

#app {
    font-family: "Poppins", sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: var(--text-primary);
    background: var(--bg-primary);

    height: 100%;
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
}

#nav {
    padding: 10px;
}

#nav a.router-link-exact-active {
    color: var(--create-button-green);
}

.container {
    max-width: 960px;
}

.home-bg {
    background: var(--bg-primary);
}

.app-bg {
    background: var(--nav-bar-app);
}

/* Navigation link visibility - prioritize Help */
/* Hide all nav links on small screens */
.nav-link-blog,
.nav-link-help,
.nav-link-about {
    display: none;
}

/* Show Help first on medium screens (768px+) */
@media (min-width: 456px) {
    .nav-link-help {
        display: inline;
    }
}

/* Show Help and About on slightly larger screens (900px+) */
@media (min-width: 550px) {
    .nav-link-about {
        display: inline;
    }
}

/* Show all links on larger screens (1024px+) */
@media (min-width: 590px) {
    .nav-link-blog {
        display: inline;
    }
}

.title-img {
    height: 35px;
    width: auto;
}

@media (max-width: 370px) {
    .title-img {
        height: 25px;
        width: auto;
    }
}

.box {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.box-2 {
    flex: 1;
}

.footer {
    padding-bottom: 20px;
}

.footer-text {
    color: var(--text-secondary);
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    padding: 0 1rem;
}

.footer-copyright {
    white-space: nowrap;
}

.footer-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
}

.footer-divider {
    color: var(--text-secondary);
}

.footer-link {
    color: var(--text-secondary) !important;
    white-space: nowrap;
}

.footer-item {
    padding: 5px;
}

.nav-button {
    border-radius: 8px !important;
    color: #fff !important;
}

.avatar-img-header {
    margin-top: -6px;
}

.account-button {
    height: 40px;
    width: 50px;
    margin-right: 5px;
    margin-left: 8px;
    /* margin: 0px !important; */
    padding: 0px !important;
}

a {
    text-decoration: none;
}
a:link {
    text-decoration: none;
}
a:visited {
    text-decoration: none;
}
a:hover {
    text-decoration: underline;
}
a:active {
    text-decoration: inherit;
}

.no-link > a {
    color: var(--text-primary) !important;
    text-decoration: none;
}
.nav-text {
    color: var(--text-primary) !important;
}

@media (max-width: 440px) {
    .theme-toggle-responsive {
        display: none !important;
    }
}
</style>
